import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useConfirm } from "@/components/ui/confirm";
import { Skeleton } from "@/components/ui/skeleton";
import { useCancelRun, useDeleteRun } from "@/hooks/useMaxwellRuns";
import { formatDateTime } from "@/lib/format";
import { humanizeState } from "@/lib/text";
import type { AgentRunDetail } from "@/types/maxwell-runs";

import { RunStatusBadge } from "./RunStatusBadge";
import { StepActionBar } from "./StepActionBar";
import { StepResultDetails } from "./StepResultDetails";
import { StepStateIcon } from "./StepStateIcon";

/** Run statuses a Cancel makes sense for — every non-terminal status.
 *  `cancel_run` itself has no status guard (it's idempotent), but offering the
 *  button on an already-terminal run is pointless. */
const CANCELLABLE_STATUSES = new Set([
  "planning",
  "awaiting_plan_approval",
  "running",
  "paused",
  "awaiting_human",
]);

/** Terminal run statuses — Delete is offered here; Cancel no longer applies. */
const TERMINAL_STATUSES = new Set(["done", "failed", "cancelled"]);

interface RunStepTimelineProps {
  detail: AgentRunDetail | undefined;
  isLoading: boolean;
  /** Fires after the shown run is deleted — the parent clears its selection so
   *  the detail pane doesn't keep pointing at a run that no longer exists. */
  onDeleted: () => void;
}

/**
 * The selected run's story: a header that leads with the goal (status, kind,
 * started time, and step progress beneath it; Cancel/Delete on the right; a
 * destructive-striped callout when the run paused on an error), then each step
 * on a timeline spine whose nodes encode step state. Reuses the run-detail
 * data (`GET /api/maxwell/runs/:id`).
 */
export function RunStepTimeline({ detail, isLoading, onDeleted }: RunStepTimelineProps) {
  const cancel = useCancelRun();
  const del = useDeleteRun();
  const confirm = useConfirm();

  if (isLoading || !detail) {
    return (
      <div className="flex flex-col gap-3 p-4">
        <Skeleton className="h-5 w-2/3" />
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-12 w-full" />
      </div>
    );
  }

  const { run, steps } = detail;
  const cancellable = CANCELLABLE_STATUSES.has(run.status);
  const terminal = TERMINAL_STATUSES.has(run.status);
  const doneCount = steps.filter((s) => s.status === "done").length;

  const onCancel = () => {
    cancel.mutate(run.id, {
      onSuccess: () => toast.success("Run cancelled"),
      onError: () => toast.error("Failed to cancel run"),
    });
  };

  const onDelete = async () => {
    const ok = await confirm({
      title: "Delete this run?",
      description:
        "The run and its step history are permanently deleted. This cannot be undone.",
      confirmLabel: "Delete",
      destructive: true,
    });
    if (!ok) return;
    del.mutate(run.id, {
      onSuccess: () => {
        toast.success("Run deleted");
        onDeleted();
      },
      onError: () => toast.error("Failed to delete run"),
    });
  };

  return (
    <div className="flex flex-col">
      <div className="flex flex-col gap-3 border-b border-border p-4">
        <div className="flex items-start justify-between gap-3">
          <p className="min-w-0 text-base font-semibold text-foreground">{run.goal}</p>
          <div className="flex shrink-0 items-center gap-2">
            {cancellable && (
              <Button
                size="sm"
                variant="outline"
                disabled={cancel.isPending}
                onClick={onCancel}
              >
                Cancel run
              </Button>
            )}
            {terminal && (
              <Button
                size="sm"
                variant="ghost"
                disabled={del.isPending}
                onClick={onDelete}
              >
                Delete
              </Button>
            )}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
          <RunStatusBadge status={run.status} />
          {run.kind === "bulk" && <Badge>Bulk</Badge>}
          <span>Started {formatDateTime(run.created_at)}</span>
          <span aria-hidden>·</span>
          <span>
            {doneCount} of {steps.length} {steps.length === 1 ? "step" : "steps"} done
          </span>
        </div>
        {run.last_error && (
          <div className="rounded-md border border-l-[3px] border-border border-l-destructive bg-card px-3 py-2">
            <p className="text-sm font-medium text-foreground">Paused on an error</p>
            <p className="mt-0.5 text-sm whitespace-pre-line text-muted-foreground">
              {run.last_error}
            </p>
          </div>
        )}
      </div>

      <ol className="flex flex-col p-4">
        {steps.map((step, i) => (
          <li key={step.id} className="relative flex gap-3 pb-5 last:pb-0">
            {i < steps.length - 1 && (
              <span
                aria-hidden
                className="absolute top-7 bottom-0 left-3 w-px -translate-x-1/2 bg-border"
              />
            )}
            <StepStateIcon status={step.status} />
            <div className="flex min-w-0 flex-1 flex-col gap-1.5 pt-0.5">
              <div className="flex min-w-0 items-center gap-2">
                <span className="truncate text-sm font-medium text-foreground">
                  {humanizeState(step.capability)}
                </span>
                {step.needs_you && <Badge variant="warning">Needs you</Badge>}
              </div>
              {step.needs_you && step.approval_notification_id && (
                <StepActionBar
                  notificationId={step.approval_notification_id}
                  isHitl={step.status === "awaiting_human"}
                />
              )}
              <StepResultDetails step={step} />
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}
